import { usePageInfo, useStateRef } from '@op/hooks/common';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { DBO_TM } from '@op/utils/format';
import { timeRangeOptions } from '@opm/components/FilterCommon/constants/timeRange';
import { timeRangeTranslate } from '@opm/components/FilterCommon/utils';
import { gotoDownload } from '@opm/pages/Download';
import { getHashtagDetailsVideoList } from '@opm/services';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SortBy } from 'tea-component/lib/table/addons';
import { PageInfoType } from '@op/types/common';

/**
 * 这个作为统一获取Video值的方法，保证Table和Card组件获取值相同
 * key为取值key，valueKey为对象实际值
 * 避免后续取值修改只改一处的问题
 * 注意，下面的key一旦定了就不可再更改
 * @param data
 * @returns
 */
const getCommonVideoValue = (data: any) => {
  return [
    { key: 'views', valueKey: 'tweets_view' },
    { key: 'likes', valueKey: 'tweets_like' },
    { key: 'comment', valueKey: 'tweets_comment' },
    { key: 'followers', valueKey: 'anchors_followers' },
    { key: 'username', valueKey: 'anchor_name' },
    { key: 'cover', valueKey: 'video_image' },
    { key: 'time', valueKey: 'video_release_time' },
    { key: 'channel', valueKey: 'channel_name' },
    { key: 'duration', valueKey: 'video_duration' },
    { key: 'url', valueKey: 'video_url' },
  ].reduce((pre, cur) => {
    const { key, valueKey } = cur;
    return {
      ...pre,
      [key]: data[valueKey],
    };
  }, data);
};

export function useHashtagDetailsVideoList({
  filter,
  defaultPageInfo = { page: 1, page_size: 40 },
  translateCategory,
}: {
  filter: any;
  defaultPageInfo?: { page: number; page_size: number };
  translateCategory: (text: string, isEn: boolean) => string;
}) {
  const [keywordRef, keyword, setKeyword] = useStateRef<string>('');
  const [sortByRef, sortBy, setSortBy] = useStateRef<SortBy<string>>({
    order: 'desc',
    by: 'tweets_view',
  });
  const {
    pageInfo,
    changePageInfo,
    resetPageInfo,
    nextPage,
    changePageInfoNoRender,
  } = usePageInfo({
    defaultPageInfo,
  });
  const [dataRef, data, setData] = useStateRef<any[][]>([]);
  const [operation, setOperation] = useState<'card' | 'table'>('card');
  const filterRef = useRef<any>(filter);

  function getListData(data: any[][]) {
    const result: Array<any> = [];
    let page = 1;
    data.every(item => {
      if (item) {
        result.push(...item);
        page = page + 1;
        return true;
      }
      return false;
    });

    return { page, result };
  }

  const [total, setTotal] = useState(0);
  const { wrapperCancelRequest, loading } = useCancelRequestHook({
    defaultLoading: true,
  });

  function resetData() {
    setData([]);
    resetPageInfo();
  }

  function changeSortBy(sortBy: SortBy<string>) {
    setSortBy(sortBy);
    resetData();
  }

  function changeKeyword(keyword: string) {
    setKeyword(keyword);
    resetData();
  }

  const params = useMemo(() => {
    if (filterRef?.current !== filter) {
      filterRef.current = filter;
      resetData();
    }
    return {
      ...filter,
      ...pageInfo,
      sort_item: sortByRef.current?.by,
      sort_model: sortByRef.current?.order,
      search_text: keywordRef.current,
    };
  }, [filter, pageInfo, operation]);

  function request() {
    if (!filter?.hashtag) return;
    wrapperCancelRequest(getHashtagDetailsVideoList, params)
      .then(data => {
        const { list, total } = data;
        const realList = list?.map((item: any) => getCommonVideoValue(item));

        if (dataRef.current) {
          dataRef.current[pageInfo.page - 1] = realList;
          setData([...dataRef.current]);
        }

        setTotal(total || 0);
      })
      .catch(() => {
        const { page } = getListData(dataRef.current || []);
        if (page > 0) {
          changePageInfoNoRender({ ...pageInfo, page: page - 1 });
        }
      });
  }

  useEffect(() => {
    const { page } = getListData(dataRef.current || []);
    if (pageInfo.page >= page) {
      request();
    }
  }, [params]);

  function download() {
    const translateTimeRange = timeRangeTranslate(timeRangeOptions);
    gotoDownload({
      key: 'HashtagVideoDownload',
      fileName: `Hashtag_Videos_${translateTimeRange(
        params.time_range,
        true,
      )}_${translateCategory(params.categories[0], true)}_${DBO_TM.timeTitle(
        params,
      )}`,
      filter: params,
    });
  }

  function changePageInfoData(localPageInfo: PageInfoType) {
    if (pageInfo.page_size !== localPageInfo.page_size) {
      setData([]);
    }
    changePageInfo(localPageInfo);
  }

  function nextPageChange() {
    const { page, page_size } = pageInfo;
    if (loading || total <= page * page_size) {
      return;
    }
    if (operation === 'table') {
      return;
    }

    nextPage();
  }

  function changeOperation(value: 'card' | 'table') {
    setOperation(value);
    if (value === 'table') {
      changePageInfo({ ...pageInfo, page: 1 });
    } else {
      const { page } = getListData(dataRef.current || []);
      changePageInfoNoRender({ ...pageInfo, page: page - 1 });
    }
  }

  const cardRecords = useMemo(() => {
    return getListData(data || []).result;
  }, [data, pageInfo]);

  const tableRecords = useMemo(() => {
    return data?.[pageInfo.page - 1] || [];
  }, [data, pageInfo]);

  return {
    data,
    cardRecords,
    tableRecords,
    total,
    pageInfo,
    changePageInfo: changePageInfoData,
    nextPage: nextPageChange,
    loading,
    sortBy,
    keyword,
    setKeyword: changeKeyword,
    setSortBy: changeSortBy,
    download,
    operation,
    setOperation: changeOperation,
    resetData,
  };
}
